import {
  BrokenImage as BrokenImageIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  Folder as FolderIcon,
} from "@mui/icons-material";
import {
  Badge,
  Collapse,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { updateStatus } from "@store/wrangler/actions";
import { FileIcon } from "@utils/additionalIcons";
import { getFileSize } from "containers/Visuals/TreeMap/treemap";
import { useProviderFiles } from "containers/hooks/useData";
import { Category } from "fileParsing/types";
import { UserInteractionStatus, WranglerItem } from "fileParsing/wrangler/types";
import { useAppDispatch } from "index";
import React, { useState } from "react";
import { WranglerProcessor } from "webworker/Wrangler/WranglerProcessor";
import { getExportWorkerByUUID } from "webworker/init";

type FileListProps = {
  name: string;
  items: WranglerItem[];
  selected?: WranglerItem;
  setSelectedItem: (ti: WranglerItem) => void;
  onConfirm: (del: boolean) => void;
  workerId?: string;
  orphan?: boolean;
};

const StatusIndicator: React.FC<{ name: string; checked?: boolean }> = ({ name, checked = false }) => (
  <Tooltip title={name}>
    <span
      style={{
        display: "inline-block",
        background: checked ? "#5b5" : "#888",
        width: 24,
        height: 24,
        margin: -2,
        borderRadius: "12px",
        color: "#cfc",
        transform: `scale(.8)${checked ? "" : " translateY(-3px)"}`,
      }}
    >
      {checked ? <CheckIcon /> : <></>}
    </span>
  </Tooltip>
);

export const FileList: React.FC<FileListProps> = ({
  name,
  items,
  selected,
  setSelectedItem,
  onConfirm,
  workerId,
  orphan = false,
}) => {
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const providerFiles = useProviderFiles(selected?.exportId ?? 0);

  const sortedFiles = items
    .map((item) => {
      const files = WranglerProcessor.matchFiles(item, providerFiles);
      return {
        item,
        size: files.reduce((sum, f) => sum + f.uncompressedSize, 0),
        points: files.reduce((sum, f) => sum + (f.data.length ?? f.data.length), 0),
      };
    })
    // Sort does work but not always: Show files with many children topmost
    // and bigger files before smaller ones
    .sort(
      (a, b) =>
        (Math.max(2, b.item.files.length) - Math.max(2, a.item.files.length)) * 100 +
        (b.size + a.size) / (b.size - a.size),
    );

  const hideItem = (item: WranglerItem) => {
    dispatch(updateStatus(item.id, UserInteractionStatus.Hidden));
    onConfirm(true);
  };

  const resolveItem = async (item: WranglerItem) => {
    let didCompile = true;

    // Compile datapoints
    if (workerId !== undefined) {
      const worker = await getExportWorkerByUUID(workerId)?.getWrangler();
      if (worker === undefined) return;

      didCompile =
        (await worker.compileNewTemplates({
          ...item,
        })) ?? false;
    }

    if (item.userStatus !== UserInteractionStatus.Automatic && item.userStatus !== UserInteractionStatus.Resolved) {
      dispatch(
        updateStatus(
          item.id,
          UserInteractionStatus.Compiled,
          {
            didCompile,
          },
          true,
        ),
      );
    } else {
      dispatch(
        updateStatus(
          item.id,
          UserInteractionStatus.Resolved,
          {
            didCompile,
          },
          true,
        ),
      );
    }

    onConfirm(false);
  };

  return (
    <ul style={{ padding: 0 }} key={`ul-${name}`}>
      {/* Subheader: Name of status */}
      <ListSubheader>
        {name}
        {/* Button to (un)collapse subsection/items */}
        <IconButton
          sx={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
          }}
          onClick={() => setCollapsed((b) => !b)}
        >
          {collapsed === false ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </ListSubheader>

      <Collapse in={!collapsed}>
        {
          // List of items with given status
        }
        {sortedFiles.map(({ item, size, points }) => (
          <ListItem
            key={`file-item-${item.id}`}
            secondaryAction={
              orphan ? undefined : (
                <Stack>
                  <IconButton edge="end" onClick={() => hideItem(item)}>
                    <DeleteIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => resolveItem(item)}>
                    <CheckCircleIcon />
                  </IconButton>
                </Stack>
              )
            }
            disablePadding
          >
            <ListItemButton
              selected={selected?.id === item.id}
              onClick={() => setSelectedItem(item)}
              sx={{ "&>*": { opacity: orphan ? 0.5 : 1 } }}
            >
              <ListItemAvatar>
                <Badge badgeContent={item.files.length > 1 ? item.files.length : undefined}>
                  <SvgIcon
                    component={orphan ? BrokenImageIcon : item.files.length > 1 ? FolderIcon : FileIcon}
                    sx={(theme) => ({
                      color: theme.palette.categories[item.category ?? Category.Other].main, // item.files?.[0]?.dataCategory ??
                    })}
                  />
                </Badge>
              </ListItemAvatar>
              <ListItemText
                secondary={
                  orphan ? (
                    <></>
                  ) : (
                    <>
                      <StatusIndicator name="File is not empty" checked={item.dataProgress.isNotEmpty} />{" "}
                      <StatusIndicator name="Found data points" checked={item.dataProgress.foundDataPoints} />{" "}
                      <StatusIndicator name="Time information found" checked={item.dataProgress.hasTimestamp} />{" "}
                      <StatusIndicator name="Data was categorized" checked={item.dataProgress.guessedCategory} />{" "}
                      <StatusIndicator name="Compiled data" checked={item.dataProgress.didCompile} />
                      <br />
                      File Size: {getFileSize(size)} | {points} Data Points
                    </>
                  )
                }
              >
                {item.name}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </Collapse>
    </ul>
  );
};
